import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Drawer, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Document_vente, GeneriqueData, SysTypeDocument, Tiers } from '../models/models';
import { TypeTiers } from '../constants/enums';
import TiersAutoC from './AutoComplete/TiersAutoC';
import SelectDepot from './Selects/SelectDepot';

export interface DuplicatePayload {
  doc_code: string;
  tiers_id: number | null;
  depot_id: number;
  reference: string | null;
  date_doc: string;
  depot_composant_id: number | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Document à dupliquer — sert à pré-remplir le formulaire à l'ouverture. */
  source: Document_vente | null;
  /** Types de document que l'utilisateur a le droit de créer. */
  types: SysTypeDocument[];
  onSubmit: (payload: DuplicatePayload) => void;
  submitting?: boolean;
}

const today = () => new Date().toISOString().split('T')[0];

const TIERS_LABEL: Record<string, string> = {
  [TypeTiers.CLIENT]: 'Client',
  [TypeTiers.FOURNISSEUR]: 'Fournisseur',
  [TypeTiers.EMPLOYE]: 'Employé',
};

/**
 * Formulaire de duplication — réglé AVANT la création (pas après) : une fois le document
 * créé, DocumentVenteForm verrouille dates/dépôt/tiers pour toute entité existante. Le
 * département reste hérité de la source, non modifiable ici (hors périmètre pour l'instant).
 */
const DuplicateDocumentDrawer: React.FC<Props> = ({
  open, onClose, source, types, onSubmit, submitting = false,
}) => {
  const [docCode, setDocCode]             = useState('');
  const [tiers, setTiers]                 = useState<Tiers | null>(null);
  const [depot, setDepot]                 = useState<GeneriqueData | null>(null);
  const [depotComposant, setDepotComposant] = useState<GeneriqueData | null>(null);
  const [reference, setReference]         = useState('');
  const [dateDoc, setDateDoc]             = useState(today());

  // Réinitialise le formulaire à chaque ouverture sur une nouvelle source. Le dépôt
  // source/composant n'est jamais pré-rempli depuis la source (pas d'hypothèse) —
  // l'utilisateur le choisit explicitement si le type cible en a besoin.
  useEffect(() => {
    if (!open || !source) return;
    const sourceCode = source.type_document?.code ?? source.doc_code ?? '';
    const codeValide  = types.some(t => t.code === sourceCode) ? sourceCode : (types[0]?.code ?? '');
    setDocCode(codeValide);
    setTiers(source.tiers ?? null);
    setDepot(source.depot ?? null);
    setDepotComposant(null);
    setReference(source.reference ?? '');
    setDateDoc(today());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source]);

  const selectedType = useMemo(() => types.find(t => t.code === docCode) ?? null, [types, docCode]);

  const handleTypeChange = (newCode: string) => {
    const newType = types.find(t => t.code === newCode) ?? null;
    // Le tiers n'est conservé que si la catégorie (client/fournisseur/employé) ne change pas —
    // sinon un id client pourrait être soumis comme fournisseur.
    if (newType?.type_tiers !== selectedType?.type_tiers) setTiers(null);
    setDocCode(newCode);
  };

  const tiersRequis    = !!selectedType?.tiers_obligatoire;
  const afficherTiers  = !!selectedType?.type_tiers && selectedType.type_tiers !== TypeTiers.AUCCUN;

  // Dépôt source (transfert) / dépôt composants (fabrication) — régénère la ligne
  // TRANSFERT/COMPOSANT contrepartie des lignes ARTICLE dupliquées. Obligatoire pour
  // TRANSFERT (aucun repli côté backend) ; optionnel pour FABRICATION (repli sur le
  // dépôt du nouveau document si omis).
  const afficherDepotComposant = !!selectedType?.est_transfert || !!selectedType?.est_fabrication;
  const depotComposantRequis   = !!selectedType?.est_transfert;
  const depotComposantLabel    = selectedType?.est_transfert ? 'Dépôt source' : 'Dépôt composant';

  const peutValider = !!docCode && !!depot?.id && !!dateDoc
    && (!tiersRequis || !!tiers?.id)
    && (!depotComposantRequis || !!depotComposant?.id);

  const handleSubmit = () => {
    if (!peutValider || !depot?.id) return;
    onSubmit({
      doc_code:           docCode,
      tiers_id:           tiers?.id ?? null,
      depot_id:           depot.id,
      reference:          reference || null,
      date_doc:           dateDoc,
      depot_composant_id: depotComposant?.id ?? null,
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, bgcolor: '#fff' } }}
    >
      <Box
        sx={{
          px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: '1px solid #e4e7ec', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: 34, height: 34, borderRadius: '10px', bgcolor: '#eef4ff', color: '#2563eb',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </Box>
        <Typography sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1rem', color: '#101828' }}>
          Dupliquer {source?.numero_doc ?? ''}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Fermer">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          select
          label="Type de document"
          size="small"
          value={docCode}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {types.map((t) => (
            <MenuItem key={t.code} value={t.code}>{t.libelle}</MenuItem>
          ))}
        </TextField>

        {afficherTiers && (
          <TiersAutoC
            type={selectedType?.type_tiers as TypeTiers}
            value={tiers}
            onTiersChange={setTiers}
            required={tiersRequis}
            label={TIERS_LABEL[selectedType?.type_tiers ?? ''] ?? 'Tiers'}
          />
        )}

        <SelectDepot
          selectedDepot={depot}
          onDepotChange={setDepot}
          departement_id={source?.departement_id ?? null}
        />

        {afficherDepotComposant && (
          <SelectDepot
            label={depotComposantLabel}
            selectedDepot={depotComposant}
            onDepotChange={setDepotComposant}
            departement_id={source?.departement_id ?? null}
          />
        )}

        <TextField
          label="Référence"
          size="small"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />

        <TextField
          label="Date document"
          type="date"
          size="small"
          value={dateDoc}
          onChange={(e) => setDateDoc(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Button variant="contained" onClick={handleSubmit} disabled={!peutValider || submitting}>
          {submitting ? 'Duplication…' : 'Dupliquer'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default DuplicateDocumentDrawer;
